import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CustomButton, CustomCheckBox, CustomInput, Text } from '../../shared/uiComponents/';
import { PRIMARY } from '../../shared/Helpers/colors';
import { errorNormalizer, validateEmail } from '../../shared/Helpers/functions';
import { LoginFunctionalities, WhiteContainer } from './styledComponents';
import Wrapper from '../../components/WrapperWithoutMenu';
import { getRedirectURL, removeRedirectURL } from '../../App/components';
import { DispatchProperties, useSelector } from '../../redux/store';
import { login } from '../../redux/State/identitySlice/accountSlice';
import { getUserBranches } from '../../redux/State/userSlice';
import { setBranchId } from '../../redux/API/Main/fetchAPI';
import { Logo } from '../../components/Logo';

const LoginContent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();

	const [email, setEmail] = useState<string>('');
	const [emailError, setEmailError] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [passwordError, setPasswordError] = useState<string>('');
	const [rememberMe, setRememberMe] = useState<boolean>(true);
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const isLoggingIn = useSelector((state) => state.account.loading);
	const isFetchingBranches = useSelector((state) => state.userBranch.isFetching);

	const onForgetClick = () => {
		navigate(`/recover-password?email=${encodeURIComponent(email)}`);
	};

	useEffect(() => {
		window.localStorage.setItem('remember', JSON.stringify(rememberMe));
	}, [rememberMe]);

	const handleEmailInputBlur = () => {
		if (Boolean(email) && !validateEmail(email)) {
			setEmailError('Please enter a valid email');
		} else {
			setEmailError('');
		}
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!email || !password) {
			if (!email) {
				setEmailError('Please enter your email');

				return;
			}

			if (!password) {
				setPasswordError('Please enter your password');
			}

			return;
		}

		dispatch(login({ email, password }))
			.then(unwrapResult)
			.then((response) => {
				const id = response?.user.id;
				if (!id) return;
				dispatch(getUserBranches(id))
					.then(unwrapResult)
					.then((responseResult) => {
						const branchId = responseResult?.length ? responseResult[0].id : '';
						setBranchId(branchId);
						const loggedUserId = window.localStorage.getItem('userId');

						const redirectURL = getRedirectURL();

						if (loggedUserId && loggedUserId === id && redirectURL) {
							removeRedirectURL();
							navigate(redirectURL);

							return;
						}
						window.localStorage.setItem('userId', id);
						navigate('/dashboard');
					});
			})
			.catch(errorNormalizer);
	};

	const onPasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setPassword(value);

		if (value) {
			setPasswordError('');
		}
	};

	const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.trim();
		setEmail(value);

		if (value) {
			setEmailError('');
		}
	};

	const onRememberMeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		setRememberMe(checked);
	};

	return (
		<WhiteContainer>
			<Text title="Login" type="h4" font="header" fontWeight="semiBold" className="marginBottom24" />
			<form onSubmit={handleSubmit}>
				<CustomInput
					value={email}
					onChange={onEmailChange}
					label="Email"
					className="marginBottom16"
					type="email"
					error={Boolean(emailError)}
					errorMessage={emailError}
					onBlur={handleEmailInputBlur}
				/>
				<CustomInput
					value={password}
					onChange={onPasswordChange}
					label="Password"
					className="marginBottom16"
					type={showPassword ? 'text' : 'password'}
					endAdornment={
						<FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} onClick={() => setShowPassword(!showPassword)} />
					}
					error={Boolean(passwordError)}
					errorMessage={passwordError}
				/>

				<LoginFunctionalities>
					<CustomCheckBox item={{ checked: rememberMe, id: '1', label: 'Remember me' }} onChange={onRememberMeChange} />
					<Text title="Forgot password?" className="link" color={PRIMARY.LIGHT[16]} onClick={onForgetClick} />
				</LoginFunctionalities>

				<CustomButton
					title="Log in"
					type="submit"
					loading={isLoggingIn || isFetchingBranches}
					disabled={Boolean(emailError) || Boolean(passwordError)}
				/>
			</form>

			<Logo />
		</WhiteContainer>
	);
};

const Login = () => <Wrapper Component={LoginContent} />;

export default Login;
